import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useDrawer } from "@/context/DrawerContext";
import { useAuth } from "@/hooks/useAuth";
import { hasRole } from "@/utils/roleUtils";
import { api } from "@/services/api";
import { SessionPrepDrawer } from "@/pages/dashboard/components/advisor-home/components/SessionPrepDrawer";

/**
 * Global SessionPrepDrawer (advisor read-only view) that can be opened from
 * anywhere in the app — e.g. clicking "View details" on a "Prep Notes
 * Submitted" / "Session Prep Ready" notification. Uses DrawerContext for
 * open/close state and fetches the advisor's meetings to find the one to show.
 */
export const UseSessionPrepDrawer: React.FC = () => {
  const { isSessionPrepDrawerOpen, sessionPrepDrawerMeetingId, closeSessionPrepDrawer } =
    useDrawer();
  const { user } = useAuth();
  const isAdvisor = hasRole(user, "advisor");

  const { data: upcomingMeetings } = useQuery({
    queryKey: ["advisor-meetings"],
    queryFn: () => api.advisors.getMeetings(),
    enabled: isAdvisor && isSessionPrepDrawerOpen,
    staleTime: 2 * 60 * 1000,
  });

  const { data: pastMeetings } = useQuery({
    queryKey: ["advisor-past-meetings"],
    queryFn: () => api.advisors.getPastMeetings(),
    enabled: isAdvisor && isSessionPrepDrawerOpen,
    staleTime: 2 * 60 * 1000,
  });

  const meeting = useMemo(() => {
    if (!sessionPrepDrawerMeetingId) return null;
    return (
      [...(upcomingMeetings ?? []), ...(pastMeetings ?? [])].find(
        (m) => m.id === sessionPrepDrawerMeetingId,
      ) ?? null
    );
  }, [sessionPrepDrawerMeetingId, upcomingMeetings, pastMeetings]);

  if (!isAdvisor) return null;

  return (
    <SessionPrepDrawer isOpen={isSessionPrepDrawerOpen} onClose={closeSessionPrepDrawer} meeting={meeting} />
  );
};
